import { useEffect, useState } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, Receipt, Package, Landmark, ChevronRight, ChevronDown, Send, Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const { hasPerm } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ov, setOv] = useState(null);
  const [fiscal, setFiscal] = useState(null);
  const canFiscal = hasPerm("fiscal.view");
  const canEmit = hasPerm("fiscal.emit_document");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [nfce, setNfce] = useState(null); // { d, step: "ask"|"cpf", cpf }
  const [notas, setNotas] = useState(null);

  const loadFiscal = () => { if (canFiscal) api.get(`/reports/fiscal?month=${month}`).then((r) => setFiscal(r.data)).catch(() => setFiscal(null)); };
  const loadNotas = () => { if (canFiscal) api.get(`/reports/fiscal-notas`).then((r) => setNotas(r.data)).catch(() => setNotas(null)); };
  useEffect(() => {
    api.get(`/reports/overview?month=${month}`).then((r) => setOv(r.data)).catch(() => setOv(null));
    loadFiscal();
    loadNotas();
  }, [month, canFiscal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualiza sozinho enquanto houver nota fiscal em processamento (NFS-e é assíncrona)
  useEffect(() => {
    const pend = (fiscal?.documents || []).filter((d) => d.emissionStatus === "processando_autorizacao" && d.fiscalRef);
    if (pend.length === 0) return;
    const iv = setInterval(async () => {
      await Promise.all(pend.map((d) => api.get(`/fiscal/refresh?ref=${encodeURIComponent(d.fiscalRef)}`).catch(() => {})));
      loadFiscal();
      loadNotas();
    }, 20000);
    return () => clearInterval(iv);
  }, [fiscal]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (d, status) => {
    try {
      await api.post("/reports/fiscal/status", { type: d.type, number: d.number, status });
      setFiscal((f) => ({ ...f, documents: f.documents.map((x) => (x.number === d.number && x.type === d.type ? { ...x, status } : x)) }));
      toast.success("Status fiscal atualizado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (d) => {
    if (expanded === d.number) { setExpanded(null); setDetail(null); return; }
    setExpanded(d.number); setDetail(null);
    try { const { data } = await api.get(`/reports/fiscal/detail?type=${encodeURIComponent(d.type)}&number=${encodeURIComponent(d.number)}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const emitNota = async (d, kind, cpf, authPassword) => {
    try {
      const q = kind ? `&kind=${kind}` : "";
      const cq = cpf ? `&cpf=${encodeURIComponent(cpf)}` : "";
      const { data } = await api.post(`/fiscal/emit?type=${encodeURIComponent(d.type)}&number=${encodeURIComponent(d.number)}${q}${cq}`, authPassword ? { auth_password: authPassword } : {});
      toast.success(data.status === "autorizado" ? "Nota fiscal autorizada com sucesso!" : `Nota enviada — status: ${data.status}`);
      loadFiscal();
      loadNotas();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? (det.mensagem || det.erros?.[0]?.mensagem || JSON.stringify(det)) : formatApiError(det));
    }
  };

  const confirmNfceCpf = async () => {
    const cpf = (nfce?.cpf || "").replace(/\D/g, "");
    if (cpf.length !== 11) { toast.error("CPF inválido. Informe 11 dígitos."); return; }
    const d = nfce.d; const authPassword = nfce.authPassword; setNfce(null);
    await emitNota(d, "nfce", cpf, authPassword);
  };

  const emitNfceSemCpf = async () => {
    const d = nfce.d; const authPassword = nfce.authPassword; setNfce(null);
    await emitNota(d, "nfce", undefined, authPassword);
  };

  const refreshNota = async (d) => {
    if (!d.fiscalRef) return;
    try {
      const { data } = await api.get(`/fiscal/refresh?ref=${encodeURIComponent(d.fiscalRef)}`);
      toast.success(data.status === "autorizado" ? "Nota autorizada!" : `Status: ${data.status}`);
      loadFiscal();
      loadNotas();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  const [emailingXmls, setEmailingXmls] = useState(false);
  const enviarXmlsContador = async () => {
    setEmailingXmls(true);
    try {
      const { data } = await api.post(`/fiscal/email-xmls?month=${month}&base=${encodeURIComponent(window.location.origin)}`);
      toast.success(`E-mail enviado ao contador (${data.to})${data.cc?.length ? ` com cópia para ${data.cc.join(", ")}` : ""} — ${data.count} nota(s).`, { duration: 8000 });
    } catch (e) {
      if (e.response?.status === 404) toast.warning("Nenhuma nota autorizada com XML neste mês.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Não foi possível enviar o e-mail.");
    } finally { setEmailingXmls(false); }
  };

  const baixarXmls = async () => {
    try {
      const res = await api.get(`/fiscal/xmls-zip?month=${month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `xmls-alphaprime-${month}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("XMLs baixados. Envie o arquivo ao seu contador (contato@conquista.cnt.br).", { duration: 8000 });
    } catch (e) {
      if (e.response?.status === 404) toast.warning("Nenhuma nota autorizada com XML neste mês.");
      else toast.error("Não foi possível gerar o ZIP de XMLs.");
    }
  };

  const checkEmpresa = async () => {    try {
      const { data } = await api.get("/fiscal/empresa");
      if (data.unavailable) { toast.info(data.message, { duration: 9000 }); return; }
      if (!data.configured) { toast.warning(data.message || "Emitente não configurado no Focus."); return; }
      const nfce = data.habilita_nfce ? "NFC-e habilitada" : "NFC-e NÃO habilitada";
      const nfse = data.habilita_nfse ? "NFS-e habilitada" : "NFS-e NÃO habilitada";
      const cert = data.certificado_valido_ate ? `Certificado até ${new Date(data.certificado_valido_ate).toLocaleDateString("pt-BR")}` : "Sem certificado";
      toast.success(`${data.nome || data.cnpj} (${data.env}) · ${nfce} · ${nfse} · ${cert}`, { duration: 8000 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Não foi possível consultar o emitente."); }
  };

  const shareWhatsApp = (d) => {
    if (!d.fiscalPublicUrl) { toast.error("Link da nota indisponível. Atualize o status após a autorização."); return; }
    const tipo = d.fiscalKind === "nfse" ? "nota fiscal de serviço" : "nota fiscal";
    const msg = `Olá! Segue a ${tipo} da sua compra na Alpha Prime:\n${d.fiscalPublicUrl}`;
    const phone = (d.customerPhone || "").replace(/\D/g, "");
    const base = phone ? `https://wa.me/55${phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const downloadDoc = async (ref, kind) => {
    try {
      const res = await api.get(`/fiscal/download/${encodeURIComponent(ref)}?doc=${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${ref}.${kind === "xml" ? "xml" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Arquivo ainda não disponível. Atualize o status da nota após a autorização."); }
  };

  const viewDoc = async (ref, kind) => {
    try {
      const res = await api.get(`/fiscal/download/${encodeURIComponent(ref)}?doc=${kind}&disposition=inline`, { responseType: "blob" });
      const ct = res.headers["content-type"] || "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: ct }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Documento ainda não disponível. Toque em Atualizar após a autorização."); }
  };

  const monthOptions = (() => {
    const out = []; const d = new Date();
    for (let i = 0; i < 12; i++) { out.push(new Date(d.getFullYear(), d.getMonth() - i, 1).toISOString().slice(0, 7)); }
    return out;
  })();
  const autorizadasNoMes = (fiscal?.documents || []).filter((d) => d.emissionStatus === "autorizado" || d.status === "emitida");

  const revChart = ov ? [
    { name: "Produtos", value: ov.revenue.products, color: "#D4AF37" },
    { name: "Serviços", value: ov.revenue.services, color: "#3B82F6" },
    { name: "Aparelhos", value: ov.revenue.devices, color: "#10B981" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Relatórios & Fiscal</h1>
          <p className="text-zinc-400 mt-1">Faturamento, lucro e reserva de impostos por mês.</p></div>
        <div><Label className="text-xs text-zinc-400 mb-1.5 block">Mês</Label>
          <Input data-testid="report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="i w-44" /></div>
      </div>

      {ov && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={TrendingUp} label="Faturamento total" value={money(ov.revenue.total)} />
          <Kpi icon={Receipt} label="Lucro bruto" value={money(ov.profit.total)} green />
          <Kpi icon={Landmark} label="Reserva de impostos" value={money(ov.tax_reserve.total)} amber />
          <Kpi icon={Package} label="Valor em estoque" value={money(ov.inventory.sale_value)} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 alpha-card p-5">
            <div className="text-white font-semibold mb-4">Faturamento por origem</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={revChart}>
                <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#121217", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{revChart.map((e) => <Cell key={e.name} fill={e.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="alpha-card p-5 space-y-3">
            <div className="text-white font-semibold mb-1">Reserva de impostos</div>
            <Row label={`Reparo (${ov.tax_reserve.repair_percent}%)`} value={money(ov.tax_reserve.repair)} />
            <Row label={`Venda (${ov.tax_reserve.sale_percent}%)`} value={money(ov.tax_reserve.sale)} />
            <div className="border-t border-white/10 pt-3"><Row label="Total a reservar" value={money(ov.tax_reserve.total)} strong /></div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 pt-2">Volume</div>
            <Row label="Vendas PDV" value={ov.counts.pdv_sales} />
            <Row label="Vendas de aparelhos" value={ov.counts.device_sales} />
            <Row label="OS concluídas" value={ov.counts.os_completed} />
          </div>
        </div>
      </>}

      {canFiscal && notas && (
        <div className="space-y-4" data-testid="notas-fiscais-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi icon={Receipt} label="NFC-e emitidas (mês atual)" value={money(notas.current_month.nfce_value)} />
            <Kpi icon={Receipt} label="NFS-e emitidas (mês atual)" value={money(notas.current_month.nfse_value)} green />
            <Kpi icon={Landmark} label="Total notas (mês atual)" value={money(notas.current_month.total_value)} amber />
            <Kpi icon={TrendingUp} label="Qtd. notas (mês atual)" value={notas.current_month.nfce_count + notas.current_month.nfse_count} />
          </div>
          <div className="alpha-card p-5" data-testid="notas-historico">
            <div className="text-white font-semibold mb-1">Histórico mensal de notas autorizadas</div>
            <div className="text-xs text-zinc-500 mb-4">Valores das NFC-e e NFS-e efetivamente emitidas com sucesso (autorizadas pela SEFAZ/Prefeitura).</div>
            {notas.history.length === 0 ? <p className="text-sm text-zinc-600">Nenhuma nota autorizada ainda.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 text-xs uppercase tracking-wider text-left border-b border-white/10">
                      <th className="py-2 pr-4">Mês</th>
                      <th className="py-2 pr-4 text-right">NFC-e (qtd)</th>
                      <th className="py-2 pr-4 text-right">NFC-e (R$)</th>
                      <th className="py-2 pr-4 text-right">NFS-e (qtd)</th>
                      <th className="py-2 pr-4 text-right">NFS-e (R$)</th>
                      <th className="py-2 text-right">Total (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notas.history.map((h) => (
                      <tr key={h.month} data-testid={`notas-row-${h.month}`} className="border-b border-white/5">
                        <td className="py-2 pr-4 text-zinc-200">{fmtMonth(h.month)}</td>
                        <td className="py-2 pr-4 text-right text-zinc-400">{h.nfce_count}</td>
                        <td className="py-2 pr-4 text-right text-zinc-200">{money(h.nfce_value)}</td>
                        <td className="py-2 pr-4 text-right text-zinc-400">{h.nfse_count}</td>
                        <td className="py-2 pr-4 text-right text-zinc-200">{money(h.nfse_value)}</td>
                        <td className="py-2 text-right text-gold-bright font-semibold">{money(h.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {canFiscal && (
        <div className="alpha-card p-5" data-testid="xmls-contador-panel">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <div className="text-white font-semibold">XMLs para o contador</div>
              <div className="text-xs text-zinc-500 mt-1">Escolha o período e baixe todos os XMLs das notas autorizadas de uma vez. Você também pode baixar o XML de cada nota na lista abaixo.</div>
              <div className="text-xs mt-2 text-zinc-400">
                <span className="text-emerald-400 font-semibold" data-testid="xmls-count">{autorizadasNoMes.length}</span> nota(s) autorizada(s) em <span className="text-zinc-200">{fmtMonth(month)}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <div>
                <Label className="text-xs text-zinc-400 mb-1.5 block">Período dos XMLs</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger data-testid="xmls-month-select" className="i h-10 w-full sm:w-48"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121217] border-gold/20 text-white max-h-72">
                    {monthOptions.map((m) => <SelectItem key={m} value={m} className="focus:bg-gold/10">{fmtMonth(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button data-testid="fiscal-xmls-contador" onClick={baixarXmls} disabled={autorizadasNoMes.length === 0}
                className="text-sm px-4 py-2.5 rounded-full font-semibold text-black disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2 justify-center"
                style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
                <Download size={15} />Baixar XMLs do mês (ZIP)
              </button>
              <button data-testid="fiscal-email-contador" onClick={enviarXmlsContador} disabled={autorizadasNoMes.length === 0 || emailingXmls}
                className="text-sm px-4 py-2.5 rounded-full font-semibold whitespace-nowrap flex items-center gap-2 justify-center border border-gold/40 text-gold-bright hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed">
                <Send size={15} />{emailingXmls ? "Enviando…" : "Enviar ao contador por e-mail"}
              </button>
              {canEmit && (
                <button data-testid="fiscal-check-emitter" onClick={checkEmpresa} className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 whitespace-nowrap">Verificar emitente</button>
              )}
            </div>
          </div>
        </div>
      )}

      {canFiscal && fiscal && (
        <div className="alpha-card p-5" data-testid="fiscal-table">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="text-white font-semibold">Documentos fiscais · {fmtMonth(fiscal.month)}</div>
              <div className="text-xs text-zinc-500 mt-1">Base <span className="text-zinc-300">{money(fiscal.total_base)}</span> · Reserva <span className="text-amber-400">{money(fiscal.total_reserve)}</span></div>
            </div>
          </div>
          <div className="space-y-2.5 max-h-[440px] overflow-y-auto pr-1">
            {fiscal.documents.length === 0 && <p className="text-sm text-zinc-600">Nenhum documento no período.</p>}
            {fiscal.documents.map((d) => {
              const light = emissionLight(d);
              const badge = emissionBadge(d);
              const canShowEmit = canEmit && !d.fiscalBlocked && !["autorizado", "processando_autorizacao"].includes(d.emissionStatus) && d.status !== "emitida" && d.status !== "nao_necessario";
              const canShowFiles = (d.emissionStatus === "autorizado" || d.status === "emitida") && d.fiscalRef;
              return (
              <div key={`${d.type}-${d.number}`} data-testid={`fiscal-row-${d.number}`} className="rounded-xl border border-white/10 bg-[#0E0E12] p-3.5">
                <div className="flex items-start gap-3">
                  <button onClick={() => openDetail(d)} data-testid={`fiscal-detail-${d.number}`} className="flex-1 min-w-0 text-left flex items-start gap-2">
                    <span data-testid={`fiscal-light-${d.number}`} title={light.label} className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${light.cls} ${light.ring}`} />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-100 font-medium">{d.number}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{d.type}</span>
                        {expanded === d.number ? <ChevronDown size={13} className="text-zinc-500" /> : <ChevronRight size={13} className="text-zinc-500" />}
                      </span>
                      <span className="block text-xs text-zinc-500 truncate mt-0.5">{d.description}{d.date ? ` · ${new Date(d.date).toLocaleDateString("pt-BR")}` : ""}</span>
                    </span>
                  </button>
                  <span data-testid={`fiscal-badge-${d.number}`} className={`text-[11px] px-2.5 py-1 rounded-full shrink-0 whitespace-nowrap ${d.financingPending ? "bg-amber-500/15 text-amber-300" : badge.cls}`}>{d.financingPending ? "Aguardando repasse" : badge.label}</span>
                </div>

                <div className="mt-2 flex items-center gap-4 text-xs pl-[22px]">
                  <span className="text-zinc-500">Base <span className="text-zinc-300">{money(d.base)}</span></span>
                  <span className="text-zinc-500">Reserva <span className="text-amber-400">{money(d.tax_reserve)}</span></span>
                </div>

                {d.financingPending && (
                  <p data-testid={`fiscal-repasse-${d.number}`} className="mt-2 pl-[22px] text-[11px] text-amber-300/90">⏳ Aguardando confirmação de repasse da financeira ({d.paymentMethod}). O faturamento e a emissão de nota fiscal são liberados após confirmar o repasse em <b>Celular no Boleto → Contratos</b>.</p>
                )}

                {d.fiscalBlocked && !d.financingPending && (
                  <p data-testid={`fiscal-blocked-${d.number}`} className="mt-2 pl-[22px] text-[11px] text-zinc-400">🔒 Aparelho não autorizado para NFC-e. Habilite no cadastro do aparelho (PJ/novo) ou emita a NF-e de entrada (usado de Pessoa Física).</p>
                )}

                {d.emissionStatus === "processando_autorizacao" && (
                  <p className="mt-2 pl-[22px] text-[11px] text-amber-300/80">Aguardando autorização da prefeitura. O XML e o comprovante ficam disponíveis após a autorização — toque em <b>Atualizar</b>.</p>
                )}

                {(d.emissionStatus === "erro_autorizacao" || d.emissionStatus === "rejeitado") && (
                  <p data-testid={`fiscal-error-${d.number}`} className="mt-2 pl-[22px] text-[11px] text-red-300/90 break-words">⚠️ {d.emissionError || "A nota foi recusada."} — corrija os dados/códigos e toque em <b>{d.kind === "os" ? "Emitir NFS-e" : "Emitir NFC-e"}</b> para reenviar.</p>
                )}

                {(canShowEmit || (canEmit && d.emissionStatus === "processando_autorizacao") || canShowFiles) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 pl-[22px]">
                    {canShowEmit && (
                      <button data-testid={`fiscal-emit-${d.number}`} onClick={() => d.kind === "os" ? emitNota(d, "nfse") : setNfce({ d, step: d.isDevice ? "auth" : "ask", cpf: "", authPassword: "" })} className="text-[11px] px-3 py-1.5 rounded-full bg-gold/15 text-gold-bright hover:bg-gold/25 whitespace-nowrap">{d.kind === "os" ? "Emitir NFS-e" : "Emitir NFC-e"}</button>
                    )}
                    {canEmit && d.emissionStatus === "processando_autorizacao" && (
                      <button data-testid={`fiscal-refresh-${d.number}`} onClick={() => refreshNota(d)} className="text-[11px] px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 whitespace-nowrap">Atualizar</button>
                    )}
                    {canEmit && d.emissionStatus === "processando_autorizacao" && (
                      <button data-testid={`fiscal-reemit-${d.number}`} onClick={() => { if (!window.confirm("Reemitir esta nota? Uma nova NFS-e será enviada ao Focus com uma nova referência. Use quando a nota ficou presa em processamento.")) return; if (d.isDevice) { setNfce({ d, step: "auth", cpf: "", authPassword: "" }); } else { emitNota(d, d.kind === "os" ? "nfse" : "nfce"); } }} className="text-[11px] px-3 py-1.5 rounded-full bg-gold/15 text-gold-bright hover:bg-gold/25 whitespace-nowrap">Reemitir</button>
                    )}
                    {canShowFiles && <>
                      <button data-testid={`fiscal-xml-${d.number}`} onClick={() => downloadDoc(d.fiscalRef, "xml")} className="text-[11px] px-3 py-1.5 rounded-full bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 whitespace-nowrap">XML</button>
                      <button data-testid={`fiscal-danfe-${d.number}`} onClick={() => viewDoc(d.fiscalRef, "danfe")} className="text-[11px] px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 whitespace-nowrap">Ver PDF</button>
                      {d.fiscalPublicUrl && (
                        <button data-testid={`fiscal-whatsapp-${d.number}`} onClick={() => shareWhatsApp(d)} className="text-[11px] px-3 py-1.5 rounded-full bg-green-500/15 text-green-300 hover:bg-green-500/25 whitespace-nowrap flex items-center gap-1"><Send size={11} />WhatsApp</button>
                      )}
                    </>}
                  </div>
                )}

                {canEmit && (
                  <div className="mt-3 pl-[22px]">
                    <Select value={d.status} onValueChange={(v) => setStatus(d, v)}>
                      <SelectTrigger data-testid={`fiscal-status-${d.number}`} className="i h-9 w-full sm:w-56"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121217] border-gold/20 text-white">
                        {Object.entries(FISCAL_STATUS).map(([k, v]) => <SelectItem key={k} value={k} className="focus:bg-gold/10">{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {expanded === d.number && (
                  <div data-testid={`fiscal-detail-panel-${d.number}`} className="mt-3 ml-[22px] p-3 rounded-lg bg-[#0A0A0C] border border-white/5 text-xs text-zinc-300 space-y-2">
                    {!detail ? <span className="text-zinc-500">Carregando…</span> : <FiscalDetail d={d} detail={detail} />}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={!!nfce} onOpenChange={(o) => !o && setNfce(null)}>
        <DialogContent className="bg-[#121217] border-gold/20 text-white sm:max-w-md" data-testid="nfce-cpf-dialog">
          <DialogHeader>
            <DialogTitle>Emitir NFC-e</DialogTitle>
          </DialogHeader>
          {nfce?.step === "auth" ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="nfce-device-alert">
                ⚠️ Esta venda contém um <b>aparelho celular</b>. Informe a senha de autorização para emitir a nota fiscal.
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Senha de autorização</Label>
                <Input data-testid="nfce-auth-input" type="password" value={nfce?.authPassword || ""}
                  onChange={(e) => setNfce((n) => ({ ...n, authPassword: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && nfce?.authPassword) setNfce((n) => ({ ...n, step: "ask" })); }}
                  placeholder="••••••" className="bg-[#0E0E12] border-white/10 text-white" />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button data-testid="nfce-auth-cancel" variant="outline" onClick={() => setNfce(null)}
                  className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Cancelar</Button>
                <Button data-testid="nfce-auth-continue" disabled={!nfce?.authPassword} onClick={() => setNfce((n) => ({ ...n, step: "ask" }))}
                  className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Continuar</Button>
              </DialogFooter>
            </div>
          ) : nfce?.step === "ask" ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-300">Deseja informar o CPF do cliente na nota?</p>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button data-testid="nfce-cpf-no" variant="outline" onClick={emitNfceSemCpf}
                  className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Não, emitir sem CPF</Button>
                <Button data-testid="nfce-cpf-yes" onClick={() => setNfce((n) => ({ ...n, step: "cpf" }))}
                  className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Sim, informar CPF</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">CPF do cliente</Label>
                <Input data-testid="nfce-cpf-input" value={nfce?.cpf || ""} inputMode="numeric" maxLength={14}
                  onChange={(e) => setNfce((n) => ({ ...n, cpf: e.target.value }))}
                  placeholder="000.000.000-00" className="bg-[#0E0E12] border-white/10 text-white" />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button data-testid="nfce-cpf-back" variant="outline" onClick={() => setNfce((n) => ({ ...n, step: "ask" }))}
                  className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Voltar</Button>
                <Button data-testid="nfce-cpf-confirm" onClick={confirmNfceCpf}
                  className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Emitir com CPF</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, green, amber }) => (
  <div className="alpha-card p-5">
    <Icon size={18} className={green ? "text-emerald-400" : amber ? "text-amber-400" : "text-gold-bright"} />
    <div className={`text-2xl font-mono-kpi font-extrabold mt-2 ${green ? "text-emerald-400" : amber ? "text-amber-400" : "text-white"}`}>{value}</div>
    <div className="text-xs text-zinc-500 mt-1">{label}</div>
  </div>
);
const Row = ({ label, value, strong }) => (<div className="flex items-center justify-between text-sm"><span className="text-zinc-400">{label}</span><span className={strong ? "text-gold-bright font-semibold" : "text-zinc-200"}>{value}</span></div>);

const fmtMonth = (m) => { const [y, mo] = (m || "").split("-"); return mo ? `${mo}/${y}` : m; };

const FISCAL_STATUS = {
  nao_necessario: { label: "Não necessário", cls: "bg-zinc-700/50 text-zinc-400" },
  pendente: { label: "Pendente de emissão", cls: "bg-amber-500/15 text-amber-400" },
  emitida: { label: "Nota fiscal emitida", cls: "bg-emerald-500/15 text-emerald-400" },
};

const emissionLight = (d) => {
  const st = d.emissionStatus;
  if (st === "autorizado") return { cls: "bg-emerald-500", ring: "shadow-[0_0_8px_rgba(16,185,129,0.8)]", label: "Autorizada pela SEFAZ ✓" };
  if (st === "processando_autorizacao") return { cls: "bg-amber-400 animate-pulse", ring: "", label: "Processando autorização…" };
  if (st === "erro_autorizacao" || st === "rejeitado") return { cls: "bg-red-500", ring: "shadow-[0_0_8px_rgba(239,68,68,0.8)]", label: d.emissionError || "Erro na emissão" };
  if (st === "cancelado") return { cls: "bg-zinc-500", ring: "", label: "Cancelada" };
  if (d.status === "emitida") return { cls: "bg-emerald-500", ring: "", label: "Nota emitida" };
  if (d.status === "nao_necessario") return { cls: "bg-zinc-600", ring: "", label: "Não necessário" };
  return { cls: "bg-amber-500/50", ring: "", label: "Pendente de emissão" };
};

const emissionBadge = (d) => {
  const st = d.emissionStatus;
  if (st === "autorizado") return { label: "Autorizada", cls: "bg-emerald-500/15 text-emerald-400" };
  if (st === "processando_autorizacao") return { label: "Em processamento", cls: "bg-amber-500/15 text-amber-300" };
  if (st === "erro_autorizacao" || st === "rejeitado") return { label: "Erro na emissão", cls: "bg-red-500/15 text-red-400" };
  if (st === "cancelado") return { label: "Cancelada", cls: "bg-zinc-600/40 text-zinc-400" };
  return { label: FISCAL_STATUS[d.status]?.label || "Pendente", cls: FISCAL_STATUS[d.status]?.cls || "bg-amber-500/15 text-amber-400" };
};

function FiscalDetail({ d, detail }) {
  const c = detail.client;
  const os = detail.service_order;
  const sale = detail.sale || {};
  return (
    <>
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <span>Data: {d.date ? new Date(d.date).toLocaleString("pt-BR") : "—"}</span>
        <span>Pagamento: {d.paymentMethod || sale.paymentMethod || "—"}</span>
        <span>Total: {money(sale.total ?? d.base)}</span>
      </div>
      {d.kind === "produto" && (d.items?.length > 0) && (
        <div><div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Produtos</div>
          {d.items.map((it, i) => <div key={`${it.name}-${i}`} className="flex justify-between"><span>{it.qty}x {it.name}</span><span>{money((it.price || 0) * (it.qty || 0))}</span></div>)}
        </div>
      )}
      {d.kind === "aparelho" && <div><div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Aparelho</div>{d.description}</div>}
      {os && (
        <div><div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Ordem de Serviço {os.number || ""}</div>
          <div>Aparelho: {(os.device?.brand || os.deviceBrand || "")} {(os.device?.model || os.deviceModel || "")} {(os.device?.imei || os.imei) ? `· IMEI ${os.device?.imei || os.imei}` : ""}</div>
          {(os.reportedIssue || os.defect || os.problem) && <div>Defeito: {os.reportedIssue || os.defect || os.problem}</div>}
          {(os.servicePerformed || os.repair || os.solution) && <div>Reparo: {os.servicePerformed || os.repair || os.solution}</div>}
          {os.chargedAmount != null && <div>Valor cobrado: {money(os.chargedAmount)}</div>}
        </div>
      )}
      {c ? (
        <div><div className="text-zinc-500 uppercase text-[10px] tracking-wider mb-1">Cliente</div>
          <div className="text-zinc-100">{c.name}</div>
          {c.phone && <div>Tel: {c.phone}</div>}
          {c.document && <div>Documento: {c.document}</div>}
          {c.email && <div>E-mail: {c.email}</div>}
          {(c.address || c.neighborhood) && <div>{[c.address, c.neighborhood, c.city].filter(Boolean).join(" · ")}</div>}
        </div>
      ) : (d.customerName && <div>Cliente: {d.customerName}</div>)}
    </>
  );
}
